<template>
    <div class="setting-content--section">
        <PasswordModal :showModal="showPasswordModal" @setPassword="onCertificatePasswordSet" @close="onPasswordModalClose"/>

        <EInvoiceMessageModal 
            :showModal="showEInvoiceMessageModal"
            :title="eInvoiceMessageTitle"
            :message="eInvoiceMessageText"
            showAnimation="lock"
            :settingsPage="true"
            @close="onEInvoiceMessageClose"
        />

        <div v-if="currentCompany.country !== 'ES'">
            <p v-translate>This feature is only available for companies based in Spain.</p>
        </div>
        <div v-if="currentCompany.country === 'ES'">
            <legend v-translate>Upload your certificate</legend>
            <hr>
            <p v-translate>Digital certificates are digital identification tools that allow you to securely carry out tax procedures.</p>
            <p v-translate>To issue digital invoices with the AEAT, you need to upload a digital certificate in .pfx or .p12 format. After uploading, you must enter the password associated with the certificate to validate your identity.</p>
            <b-alert show class="gk-alert m-0" style="font-size: 1rem;" variant="warning">
                {{ $gettext('Invoices sent with Gekko will be included in the general regime (regime key 01) and will always be taxable, non-exempt operations (with or without the reverse charge mechanism).') }}
                <a class="text-info" :href="$gettext('/verifactu-declaration')">{{ $gettext('View the responsible declaration for Verifactu.') }}</a>
            </b-alert>
            <br />

            <div v-if="invoice_settings.companysettings.verifactu_certificate_uploaded">
                <div class="mb-2">
                    <i class="text-info" v-translate>You already have a certificate uploaded.</i>
                </div>

                <GekkoButton 
                    @click="showReplaceCertificateBox = true" 
                    :text="$gettext('Replace certificate')"
                    variant="outline-primary" />

                <GekkoButton 
                    :text="$gettext('Delete')"
                    icon="fa-solid fa-trash"
                    variant="link text-danger"
                    v-gk-confirm="{
                        title: $gettext('Delete certificate'),
                        message: $gettext('By confirming this action, your digital certificate will be permanently removed from Gekko\'s servers.'),
                        options: {
                            warning: $gettext('You will no longer be able to issue or manage invoices with the AEAT.'),
                            variant: 'danger',
                            confirmText: $gettext('Continue')
                        },
                        callback: () => { onCerfificateDelete() }
                    }" />
            </div>

            <div v-if="!invoice_settings.companysettings.verifactu_certificate_uploaded || showReplaceCertificateBox">
                <VueDropzone ref="certificate-dropzone" :allowed-files="'all'" :onlyChange="true" @change="fileChanged" class="mt-2" />
            </div>

            <br />

            <legend v-translate>Set Verifactu as default</legend>
            <hr>
            <p v-translate>By selecting this checkbox, all invoices you issue will automatically be submitted to the tax authorities and will include a QR code for traceability and authentication. If you prefer to exclude this option for specific invoices, you can uncheck it on the invoice page before sending the invoice.</p>
            <b-form-group>
                <b-form-checkbox v-model="invoice_settings.companysettings.make_invoice_verifactu" @change="makeInvoiceVerifactu" switch>
                    <span v-translate>Make every sent invoice Verifactu</span>
                </b-form-checkbox>
            </b-form-group>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import VueDropzone from "@/components/VueDropzone"
import GekkoButton from "@/components/GekkoButton"
import PasswordModal from "@/components/ui/PasswordModal"
import settingsPageSetup from "@/mixins/settingsPageSetup"
import CompanySettingService from "@/store/services/companysetting.service.js"
import EInvoiceMessageModal from "@/components/invoices/ui/EInvoiceMessageModal"

export default {
    name: "VerifactuSettings",
    components: {
        VueDropzone,
        GekkoButton,
        PasswordModal,
        EInvoiceMessageModal
    },
    mixins: [settingsPageSetup],
    data () {
        return {
            certificateFile: null,
            showPasswordModal: false,
            showReplaceCertificateBox: false,
            invoice_settings: {},
            pageSettings: {
                title: this.$gettext("Verifactu")
            },
            showEInvoiceMessageModal: false,
            eInvoiceMessageTitle: '',
            eInvoiceMessageText: ''
        }
    },
    computed: {
        ...mapGetters({
            currentCompany: "main/getCurrentCompany"
        })
    },
    created () {
        this.setUpData()
    },
    methods: {
        ...mapActions({
            refreshCompanyData: "main/refreshCompanyData"
        }),
        setUpData() {
            this.invoice_settings = Object.assign({}, this.currentCompany)
            console.log('setup..', this.invoice_settings)
        },
        fileChanged(file) {
            this.certificateFile = file
            if (file) this.showPasswordModal = true
        },
        onCertificatePasswordSet(password) {
            this.showPasswordModal = false
            
            const fileData = {
                'file': this.certificateFile,
                'password': password
            }

            CompanySettingService.uploadDigitalCertificate(this.invoice_settings.companysettings.id, fileData).then((response) => {
                this.$store.commit('setAlert', {
                    title: this.$gettext("Sucess"),
                    text: this.$gettext("Your certificate has been uploaded."),
                    variant: 'success',
                    duration: 4
                })

                this.refreshCompanyData().then(() => {
                    this.setUpData()
                })
                this.certificateFile = null
                this.showReplaceCertificateBox = false
            }).catch(() => {
                this.$store.commit('setAlert', {
                    title: this.$gettext("Error"),
                    text: this.$gettext("We could not process your certificate."),
                    variant: 'danger',
                    duration: 4
                })
                this.$refs['certificate-dropzone'].removeImage()
                this.certificateFile = null
            })
        },
        onPasswordModalClose() {
            this.showPasswordModal = false
            this.certificateFile = null
            this.showReplaceCertificateBox = false
            this.$refs['certificate-dropzone'].removeImage()
        },
        onCerfificateDelete() {
            CompanySettingService.deleteCertificate(this.invoice_settings.companysettings.id).then(() => {
                this.$store.commit('setAlert', {
                    title: this.$gettext("Success"),
                    text: this.$gettext("Your certificate has been deleted."),
                    variant: 'success',
                    duration: 4
                })

                this.refreshCompanyData().then(() => {
                    this.setUpData()
                })
            }).catch(() => {
                this.$store.commit('setAlert', {
                    title: this.$gettext("Error"),
                    text: this.$gettext("We could not delete your certificate."),
                    variant: 'danger',
                    duration: 4
                })
            })
        },
        makeInvoiceVerifactu() {
            const data = {
                id: this.currentCompany.companysettings.id,
                company: this.currentCompany.id,
                make_invoice_verifactu: this.invoice_settings.companysettings.make_invoice_verifactu
            }

            CompanySettingService.update(data).then(() => {
                let msg = this.$gettext("The invoices you create will no longer be marked as Verifactu.")
                if (this.invoice_settings.companysettings.make_invoice_verifactu) {
                    msg = this.$gettext("The invoices you create will be marked as Verifactu by default.")
                }

                this.$store.commit('setAlert', {
                    title: this.$gettext("Success"),
                    text: msg,
                    variant: 'success',
                    duration: 4
                })
            }).catch((error) => {
                if (error.data && error.data.make_invoice_verifactu) {
                    this.showEInvoiceMessageModal = true
                    this.eInvoiceMessageTitle = this.$gettext('Upload certificate')
                    this.eInvoiceMessageText = this.$gettext('You must upload your digital certificate to activate this feature. Simply drag and drop your file into the area above.')
                } else {
                    this.$store.commit('setAlert', {
                        title: this.$gettext("Error uploading the certificate"),
                        text: error.data,
                        variant: 'danger',
                        duration: 4
                    })
                }                
            })
        },
        onEInvoiceMessageClose() {
            this.showEInvoiceMessageModal = false
            this.eInvoiceMessageTitle = ''
            this.eInvoiceMessageText = ''
            this.showAdjustModal = false
        }
    }
}
</script>